import { FLAGS } from '@iheartjane/feature-flags';
import { IneligibleDealBanner, WizardButton } from '@iheartjane/menu';
import { Flex, JaneGoldIcon, Typography } from '@iheartjane/reefer';
import { registerJdid as registerJdidToRum } from '@iheartjane/tracking';
import { useEffect } from 'react';
import { useLoaderData } from 'react-router';

import { BloomWizardModal } from '@jane/bloom/components/bloomWizardModal';
import { BloomGoldCalloutBanner } from '@jane/bloom/components/janeGold/bloomGoldCalloutBanner';
import { JaneGoldGrid } from '@jane/bloom/components/menuRow/janeGoldGrid';
import {
  FilterSelector,
  MobileFilterRow,
} from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { CategoryTab } from '@jane/bloom/components/routes/category/components/categoryTab';
import { SeoHeading } from '@jane/bloom/components/seoHeading';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useMenuTopRowWithServerData } from '@jane/bloom/hooks/dm/useMenuTopRowWithServerData';
import { BloomFilterProvider } from '@jane/bloom/hooks/filters';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useShowInlineWizardCta } from '@jane/bloom/hooks/useShowInlineWizardCta';
import type { RecommendedSortResults } from '@jane/bloom/loaders/products/recommendedSortResults.server';
import { createMetaVariables } from '@jane/bloom/resources/meta/createMetaVariables';
import { DEFAULT_TITLE } from '@jane/bloom/resources/meta/routes/janeGold';
import { WIZARD_SOURCE } from '@jane/bloom/services/tracking/Events';
import { registerJdid } from '@jane/bloom/services/tracking/mixpanel';
import { trackSearchedMenuProducts } from '@jane/bloom/services/tracking/track';
import { isJaneUser } from '@jane/bloom/types/schemas/marketplace/janeUser';

import styles from './janeGoldRouteComponent.module.css';

export function JaneGoldRouteComponent() {
  const { cart, featureFlags, metaProps, pinnedStore, whoami } =
    useRootLoaderData();
  const showInlineWizardCta = useShowInlineWizardCta();
  const authenticated = isJaneUser(whoami.user);
  const {
    availableFilters,
    brandSpecials,
    category,
    customCategoryLabels,
    customLanguage,
    enabledSpecialsForRow: enabledStoreSpecials,
    hasLinkedBank,
    janeGold,
    jdid,
    menuProductImgsAboveTheFold,
    menuTopRow,
    recommendedSortResults,
    searchCriteria,
    searchFilterWithoutBrands,
    sort: { currentSort, sortOptions },
    storeId,
  } = useLoaderData<RecommendedSortResults>();
  const rowInstance = useMemoizedRowInstance(recommendedSortResults);

  // Handles server-side initial load and client-side refetching of the menu top row
  const {
    pauseRefetching,
    rowInstance: menuTopRowInstance,
    unpauseRefetching,
  } = useMenuTopRowWithServerData({
    initialMenuTopRow: menuTopRow,
    searchFilter: searchFilterWithoutBrands,
    storeId,
  });

  useEffect(() => {
    registerJdid(jdid);
    registerJdidToRum(jdid);
  }, [jdid]);

  useEffect(() => {
    if (!rowInstance) return;
    trackSearchedMenuProducts({
      hits: rowInstance?.totalProducts,
      searchCriteria,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchCriteria), rowInstance?.totalProducts]);

  return (
    <>
      <SeoHeading
        customSEOPage={{}}
        defaultTitle={DEFAULT_TITLE}
        metaVariables={createMetaVariables(metaProps)}
      />
      <div className={styles['janeGoldSection']}>
        <IneligibleDealBanner />
      </div>
      {brandSpecials.length > 0 && (
        <div className={styles['janeGoldSection']}>
          <Flex alignItems="center" pt={24} mb={16}>
            <JaneGoldIcon color="gold" />
            <Typography as="h2" variant="header-bold" color="text-main" ml={8}>
              Jane Gold rewards
            </Typography>
          </Flex>
          <BloomGoldCalloutBanner
            hasLinkedBank={hasLinkedBank ?? false}
            isSignedIn={!!authenticated}
            storeState={pinnedStore?.state}
            mb={24}
          />
          <JaneGoldGrid
            authenticated={authenticated}
            brandSpecials={brandSpecials}
          />
        </div>
      )}
      <BloomFilterProvider
        availableFilters={availableFilters}
        searchCriteria={searchCriteria}
      >
        <BloomWizardModal
          brandSpecials={brandSpecials}
          specials={enabledStoreSpecials}
        >
          {({ openModal }) => (
            <>
              <div className="filters-container">
                <FilterSelector
                  actionSlot={
                    showInlineWizardCta ? (
                      <WizardButton
                        onClickStart={() => openModal(WIZARD_SOURCE.BUTTON)}
                      />
                    ) : undefined
                  }
                  availableProductsCount={rowInstance?.totalProducts}
                  sortOptions={sortOptions}
                  currentSort={currentSort}
                  shouldShowGold={false}
                  hasJaneGoldProducts={janeGold.hasJaneGoldProducts}
                  showCompare={featureFlags[FLAGS.c1CompareProducts]}
                />
              </div>

              <MobileFilterRow
                actionSlot={
                  showInlineWizardCta && category ? (
                    <WizardButton
                      onClickStart={() => openModal(WIZARD_SOURCE.BUTTON)}
                    />
                  ) : undefined
                }
                availableProductsCount={rowInstance?.totalProducts}
                sortOptions={sortOptions}
                currentSort={currentSort}
                shouldShowGold={janeGold.shouldShowGold}
                hasJaneGoldProducts={janeGold.hasJaneGoldProducts}
                showCompare={featureFlags[FLAGS.c1CompareProducts]}
              />

              <CategoryTab
                pauseRefetching={pauseRefetching}
                unpauseRefetching={unpauseRefetching}
                brandSpecials={brandSpecials}
                cart={cart}
                category={category}
                customCategoryLabels={customCategoryLabels}
                customLanguage={customLanguage}
                menuTopRowInstance={menuTopRowInstance}
                openWizard={openModal}
                preloadedProductIds={menuProductImgsAboveTheFold.map(
                  ({ id }) => id
                )}
                rowInstance={rowInstance}
                specials={enabledStoreSpecials}
                janeGold={janeGold}
                pinnedStore={pinnedStore}
                enabledSpecialsForRow={enabledStoreSpecials}
              />
            </>
          )}
        </BloomWizardModal>
      </BloomFilterProvider>
    </>
  );
}
